import { useCallback, useRef, useState } from 'react'

interface UsePullToRefreshOptions {
    /** Minimum pull distance (px) to trigger refresh. Default: 60 */
    threshold?: number
    /** Called when the pull threshold is met and the user releases */
    onRefresh: () => void
}

interface UsePullToRefreshResult {
    /** Whether the user has pulled past the threshold (show "release to refresh") */
    isPulling: boolean
    /** Bind these to the scrollable container element */
    handlers: {
        onTouchStart: (e: React.TouchEvent) => void
        onTouchMove: (e: React.TouchEvent) => void
        onTouchEnd: () => void
    }
}

/**
 * Reusable pull-to-refresh hook for mobile scroll containers.
 * Only activates when the container is scrolled to the top.
 *
 * @example
 * const { isPulling, handlers } = usePullToRefresh({ onRefresh: refresh })
 * <div ref={containerRef} {...handlers}>
 *     {isPulling && <span>Release to refresh</span>}
 *     ...
 * </div>
 */
export function usePullToRefresh({ threshold = 60, onRefresh }: UsePullToRefreshOptions): UsePullToRefreshResult {
    const [isPulling, setIsPulling] = useState(false)
    const touchStartY = useRef(0)
    const isTracking = useRef(false)
    const onRefreshRef = useRef(onRefresh)
    onRefreshRef.current = onRefresh

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        const target = e.currentTarget as HTMLElement
        // Find the nearest scrollable ancestor (the actual scroll container)
        const scrollParent = target.closest('.mobile-root__content') as HTMLElement | null
        const scrollTop = scrollParent ? scrollParent.scrollTop : target.scrollTop
        // Only track if scrolled to top
        if (scrollTop <= 0) {
            touchStartY.current = e.touches[0].clientY
            isTracking.current = true
        }
    }, [])

    const onTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (!isTracking.current) return

            const target = e.currentTarget as HTMLElement
            const scrollParent = target.closest('.mobile-root__content') as HTMLElement | null
            const scrollTop = scrollParent ? scrollParent.scrollTop : target.scrollTop
            // Stop tracking if user scrolled down
            if (scrollTop > 0) {
                isTracking.current = false
                setIsPulling(false)
                return
            }

            const diff = e.touches[0].clientY - touchStartY.current
            setIsPulling(diff >= threshold)
        },
        [threshold],
    )

    const onTouchEnd = useCallback(() => {
        if (isPulling) {
            onRefreshRef.current()
        }
        setIsPulling(false)
        isTracking.current = false
    }, [isPulling])

    return {
        isPulling,
        handlers: { onTouchStart, onTouchMove, onTouchEnd },
    }
}
